import posthog from 'posthog-js'

// Initialise PostHog product analytics for the web app.
//
// The project API key is public/write-only (safe to ship in the bundle) and comes
// from VITE_POSTHOG_KEY so it stays out of git like the Supabase/Sentry keys. When
// it's absent PostHog stays inert rather than throwing, so the app still runs.
//
// Region: our project is in the EU, so the host defaults to eu.i.posthog.com.
// SPA pageviews are captured manually on route change (see PostHogTracker), so
// automatic pageview capture is disabled here to avoid double counting.
//
// CONSENT (opt-in): PostHog is initialised opted-OUT and sets no cookies until the
// visitor accepts. The choice is stored in a first-party cookie scoped to
// `.reciperoll.app` — shared with the marketing site — and PostHog uses a cross-
// subdomain cookie, so a choice (and a signed-in visitor's identity) carries
// between reciperoll.app and my.reciperoll.app. No double-prompting across the two.
const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined
const host =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ??
  'https://eu.i.posthog.com'

const CONSENT_COOKIE = 'rr_analytics_consent'
export type Consent = 'granted' | 'denied'

// Share cookies across reciperoll.app + subdomains in prod; host-only on localhost.
function cookieDomain(): string | null {
  return location.hostname.endsWith('reciperoll.app') ? '.reciperoll.app' : null
}
export function getConsent(): Consent | null {
  const m = document.cookie.match(/(?:^|;\s*)rr_analytics_consent=(granted|denied)/)
  return m ? (m[1] as Consent) : null
}
function setConsentCookie(value: Consent): void {
  const parts = [
    `${CONSENT_COOKIE}=${value}`,
    'path=/',
    `max-age=${60 * 60 * 24 * 180}`, // 180 days
    'SameSite=Lax',
  ]
  const domain = cookieDomain()
  if (domain) parts.push(`domain=${domain}`)
  if (location.protocol === 'https:') parts.push('Secure')
  document.cookie = parts.join('; ')
}

// Let the tracker + banner react when the choice changes mid-session.
const listeners = new Set<() => void>()
export function onConsentChange(fn: () => void): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

let started = false

export function initPostHog() {
  if (started || !key) return
  started = true

  posthog.init(key, {
    api_host: host,
    capture_pageview: false,
    person_profiles: 'identified_only',
    persistence: 'localStorage+cookie',
    cross_subdomain_cookie: true,
    opt_out_capturing_by_default: true, // nothing captured / no cookies until consent
  })

  // Honour a prior choice (possibly made on the marketing site) on this load.
  if (getConsent() === 'granted') posthog.opt_in_capturing()
}

export function grantConsent() {
  setConsentCookie('granted')
  posthog.opt_in_capturing()
  posthog.capture('$pageview') // the current page; the route effect handles the rest
  listeners.forEach((fn) => fn())
}

export function denyConsent() {
  setConsentCookie('denied')
  posthog.opt_out_capturing()
  listeners.forEach((fn) => fn())
}

export { posthog }
